import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Mic, Square, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VoiceNote = ({ onTranscription }) => {
  const { getAuthHeader } = useAuth();
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await transcribe(blob);
      };

      mediaRecorder.start();
      setRecording(true);
    } catch (err) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const transcribe = async (blob) => {
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'voice-note.webm');
      const response = await axios.post(
        `${API_URL}/api/transcribe`,
        formData,
        {
          ...getAuthHeader(),
          headers: {
            ...getAuthHeader().headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (response.data.text) {
        onTranscription(response.data.text);
        toast.success('Voice note transcribed');
      }
    } catch (err) {
      console.error('Transcription error:', err);
      toast.error('Failed to transcribe voice note');
    } finally {
      setTranscribing(false);
    }
  };

  if (transcribing) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled
        className="gap-2 text-slate-500"
        data-testid="voice-transcribing"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Transcribing...
      </Button>
    );
  }

  if (recording) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={stopRecording}
        className="gap-2 text-red-600 border-red-300 hover:bg-red-50 animate-pulse"
        data-testid="voice-stop-button"
      >
        <Square className="w-4 h-4" />
        Stop Recording
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={startRecording}
      className="gap-2 text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"
      data-testid="voice-record-button"
    >
      <Mic className="w-4 h-4" />
      Voice Note
    </Button>
  );
};

export default VoiceNote;
